import React from 'react';
import { ROUTER_COOKIES } from 'app/AppRouter';
import { Container, Flex, Li, Link, MarginBox, Paragraph, Section, SectionHeader, Text, Ul } from 'UI';
import { useResetScroll } from 'utils/hooks/useResetScroll';
import { Country } from 'utils/i18n/Country';

export const PersonalDataES = () => {
  useResetScroll();
  return (
    <Container size={'md'} alignText={'justify'}>
      <Flex direction={'column'}>
        <MarginBox mt={15} />
        <Flex direction={'column'} align={'center'}>
          <Text type={'h5_bold'}>RPARTSTORE</Text>
          <Text type={'h5_bold'}>AVISO SOBRE</Text>
          <Text type={'h5_bold'}>PROTECCIÓN DE DATOS PERSONALES</Text>
          <MarginBox mt={30} />
        </Flex>
        <Section>
          <Paragraph>
            El sitio web RPARTSTORE, al que se puede acceder en{' '}
            <Link to={'https://rpartstore.renault.com'} external newTab>
              {'https://rpartstore.renault.com'}
            </Link>{' '}
            (&quot;la Plataforma&quot;), ha sido creado para facilitar la consulta de la documentación técnica posventa
            de repuestos y accesorios Renault. (&quot;los Productos&quot;), así como para servir de foro para que
            vendedores (&quot;los Vendedores&quot;) que lo deseen puedan ofrecer Productos para la venta a través de un
            sitio web a compradores (&quot;los Compradores&quot;), de acuerdo con las Condiciones Generales de la
            Plataforma. Vendedores y Compradores recibirán en adelante, colectivamente, la denominación de
            &quot;Usuarios&quot;.
          </Paragraph>
          <Paragraph>
            El objetivo de este aviso sobre protección de datos es garantizar la transparencia en relación con el
            tratamiento de los datos personales que se recojan como consecuencia del uso de la Plataforma.
          </Paragraph>
        </Section>
        <Section>
          <SectionHeader title={'¿Para qué se utilizan sus datos y durante cuánto tiempo?'} />
          <Paragraph>
            Cuando se utiliza la Plataforma, sus datos personales son tratados con los fines siguientes:
          </Paragraph>
          <Ul>
            <Li>
              <Text type={'text'}>
                <strong>Gestionar sus derechos de acceso y autorización en la Plataforma</strong> sobre la base de
                nuestro interés legítimo en gestionar la confidencialidad y la seguridad de sus datos personales.
                Sometemos estos datos a tratamiento durante el tiempo en que esté abierta su cuenta de usuario, a menos
                que haya estado inactiva.
              </Text>
            </Li>
            <Li>
              <Text type={'text'}>
                <strong>Gestionar la seguridad y el buen funcionamiento de las aplicaciones y redes sobre la</strong>{' '}
                base de nuestro interés legítimo en garantizar la seguridad informática de la Plataforma. Trataremos tus
                datos durante 30 días en nuestras bases de datos.
              </Text>
            </Li>
            <Li>
              <Text type={'text'}>
                <strong>
                  Responder a sus solicitudes de ejercicio de sus derechos en relación con sus datos personales
                </strong>
                {', '}de acuerdo con las obligaciones a las que estamos sujetos por ley o cualquier normativa. Sus datos
                personales se conservarán durante el tiempo necesario para dar respuesta a su solicitud. Sin embargo,
                debemos conservar prueba registrada de nuestra respuesta al ejercicio de sus derechos (incluidos, en su
                caso, los datos personales que hayamos recopilado para este fin) para poder responder ante cualquier
                acción judicial que pudiera interponerse, durante el periodo de prescripción previsto por la legislación
                aplicable.
              </Text>
            </Li>
          </Ul>
          <Paragraph>
            Asimismo, la Plataforma utiliza cookies y otras tecnologías de rastreo para gestionar su funcionamiento y
            seguridad, así como para medir la audiencia y el rendimiento de la Plataforma, de acuerdo con las
            condiciones establecidas en la Política de Cookies aplicable a la Plataforma, a las que se puede{' '}
            <Link to={ROUTER_COOKIES(Country.ES.key)} external newTab>
              acceder desde aquí
            </Link>
            .
          </Paragraph>
        </Section>
        <Section>
          <SectionHeader title={'¿Qué datos personales se tratan?'} />
          <Paragraph>
            Los datos personales que recogemos dependen de nuestra interacción con usted e incluyen datos relacionados
            con:
          </Paragraph>
          <Ul>
            <Li>
              <Text type={'text'}>
                Su identidad y datos de contacto (apellidos, nombre, dirección postal y electrónica profesional).
              </Text>
            </Li>
            <Li>
              <Text type={'text'}>Identificadores técnicos (número IPN).</Text>
            </Li>
            <Li>
              <Text type={'text'}>
                Datos de conexión (registros de actividad, cookies) de acuerdo con la Política de{' '}
                <Link to={ROUTER_COOKIES(Country.ES.key)} external newTab>
                  Cookies de la Plataforma
                </Link>
                .
              </Text>
            </Li>
          </Ul>
          <MarginBox mt={15} />
        </Section>
        <Section>
          <SectionHeader title={'¿Quién realiza el tratamiento de sus datos personales?'} />
          <Paragraph>
            Las operaciones de tratamiento descritas anteriormente son realizadas por Renault SAS, en su caso, en
            colaboración con la filial Renault que opere en su país (*). Además, los Vendedores, miembros de la red
            principal de esta última, encargados de las actividades de distribución, participarán en la gestión de sus
            derechos de acceso y autorización en la Plataforma mediante la creación de perfiles de Usuario en los
            directorios internos y las herramientas de autenticación del Grupo Renault.
          </Paragraph>
          <Paragraph>
            <i>
              (*) Para conocer la identidad legal de la filial Renault que opera en su país, visite{' '}
              <Link to={'https://www.renaultgroup.com/en/directory/'} external newTab>
                nuestro directorio
              </Link>
              , seleccione su país en la lista y consulte su información legal.
            </i>
          </Paragraph>
          <Paragraph>
            <i>
              Renault SAS tiene su domicilio en Francia, 122-122 bis Avenue du Général Leclerc - 92100
              Boulogne-Billancourt. Los datos de contacto de su delegado de protección de datos (DPD) son los
              siguientes: Renault SAS, Direction juridique – Délégué à la Protection des Données, 122-122 bis Avenue du
              Général Leclerc – 92 100 Boulogne-Billancourt.
            </i>
          </Paragraph>
        </Section>
        <Section>
          <SectionHeader title={'¿Quién tienen acceso a sus datos personales?'} />
          <Paragraph>
            Como parte de nuestras operaciones de tratamiento de datos, solo pueden acceder a sus datos personales el
            personal interno debidamente autorizado y nuestros proveedores de servicios externos de confianza, que
            actuarán como encargados del tratamiento, siempre siguiendo nuestras instrucciones y por cuenta nuestra.
            Además, podremos comunicar sus datos personales a terceros para el cumplimiento de cualquier obligación
            legal o decisión administrativa o judicial.
          </Paragraph>
          <Paragraph>
            Debido a que algunos de estos proveedores de servicios o sus subcontratistas están ubicados en países fuera
            de la UE/EEE, hemos acordado aplicar medidas de seguridad adecuadas mediante la firma de Cláusulas
            Contractuales Tipo (SCC).
          </Paragraph>
        </Section>
        <Section>
          <SectionHeader title={'¿Cuáles son sus derechos en relación con sus datos personales?'} />
          <Paragraph>
            De conformidad con la normativa aplicable, respecto de las actividades de tratamiento descritas en este
            aviso, usted tiene:
          </Paragraph>
          <Ul>
            <Li>
              <Text type={'text'}>
                <strong>Derecho de acceso:</strong> para obtener información sobre el tratamiento de sus datos
                personales (conocer, en particular, qué datos se utilizan, con qué finalidad, etc.) y una copia de
                dichos datos.
              </Text>
            </Li>
            <Li>
              <Text type={'text'}>
                <strong>Derecho de rectificación:</strong> obtener la rectificación de sus datos personales en el caso
                de que no sean exactos o estén incompletos.
              </Text>
            </Li>
            <Li>
              <Text type={'text'}>
                <strong>Derecho de supresión:</strong> obtener la supresión de sus datos personales, si bien este
                derecho puede limitarse si fuera necesario para permitir el cumplimiento de nuestras obligaciones
                contractuales o legales.
              </Text>
            </Li>
            <Li>
              <Text type={'text'}>
                <strong>Derecho de oposición:</strong> oponerse al uso de algunos de sus datos personales. Deberá
                justificarlo dando razones específicas relacionadas con su situación particular.
              </Text>
            </Li>
            <Li>
              <Text type={'text'}>
                <strong>Derecho a la limitación del tratamiento:</strong> restringir el uso de sus datos, en particular
                si duda de la exactitud de sus datos personales, se ha opuesto al tratamiento de sus datos personales o
                necesita estos datos, por ejemplo, para ejercer o defender un derecho, y desea asegurarse de que esos
                datos no van a ser suprimidos (p. ej., en caso de que haya finalizado el periodo de conservación).
              </Text>
            </Li>
          </Ul>
          <Paragraph>
            Puede ejercer todos sus derechos enviando su solicitud a:{' '}
            <Link to={'mailto:dpo@renault.com'} external newTab>
              dpo@renault.com
            </Link>
            . Si no recibiera contestación o una respuesta satisfactoria de Renault, puede presentar una reclamación
            ante la autoridad de control de su país.
          </Paragraph>
        </Section>
        <Flex justify={'flex-end'}>
          <Text type={'text'}>Versión actualizada: Enero 2024</Text>
        </Flex>
        <MarginBox mt={90} />
      </Flex>
    </Container>
  );
};
